import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur de connexion');
      }

      // Save token and admin info
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_info', JSON.stringify(data.admin));

      toast.success(`Bienvenue ${data.admin.name} !`);
      navigate('/admin/dashboard');

    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center px-4 sm:px-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Litige-Protect <span className="text-accent">Admin</span>
        </h1>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Logo/Icon */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
              <Lock className="w-8 h-8 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Connexion Admin
            </h2>
            <p className="text-sm text-foreground/60 mt-2">
              Accédez au tableau de bord administrateur
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@litige-protect.com"
                    className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium mb-2">Mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-12 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          {/* Register link */}
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <p className="text-sm text-foreground/80 mb-2">
              Vous êtes une entreprise ?
            </p>
            <button
              onClick={() => navigate('/admin/register')}
              className="w-full h-10 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm font-medium"
            >
              Créer un compte entreprise
            </button>
          </div>

          {/* Back link */}
          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-foreground/60 hover:text-accent"
            >
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
